import React, { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useAdminAuth } from './AdminAuthContext';
import { Lock, Loader2, AlertCircle, Eye, EyeOff } from 'lucide-react';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const startGoogleAdminLogin = () => {
  const redirectUrl = window.location.origin + '/admin/auth/callback';
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

// Inline Google "G" so we don't add a new icon dependency.
const GoogleIcon = ({ className = 'w-4 h-4' }) => (
  <svg className={className} viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z" />
    <path fill="#FF3D00" d="m6.306 14.691 6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z" />
    <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 34.91 26.715 36 24 36c-5.202 0-9.619-3.317-11.282-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z" />
    <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z" />
  </svg>
);

const AdminLogin = () => {
  const { login, admin, loading } = useAdminAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  if (!loading && admin) {
    return <Navigate to={admin.must_change_password ? '/admin/change-password' : '/admin/dashboard'} replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const me = await login(email.trim().toLowerCase(), senha);
      navigate(me.must_change_password ? '/admin/change-password' : '/admin/dashboard', { replace: true });
    } catch (err) {
      setError(err?.response?.data?.detail || 'Falha ao entrar. Verifique suas credenciais.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center px-4">
      <div className="w-full max-w-sm bg-white rounded-lg shadow-md border border-[#E2E8F0] p-6">
        <div className="text-center mb-5">
          <div className="w-12 h-12 mx-auto bg-[#1E3A5F] rounded-md flex items-center justify-center mb-3">
            <Lock className="w-5 h-5 text-white" />
          </div>
          <h1 className="text-xl font-black text-[#0F1C2E]">Painel Admin</h1>
          <p className="text-xs text-[#475569] mt-1">Tô de Olho · acesso restrito</p>
        </div>

        {error && (
          <div className="flex items-start gap-2 p-2.5 mb-3 rounded bg-[#FEE2E2] border border-[#FECACA] text-[#7F1D1D] text-xs" data-testid="admin-login-error">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" /> <span>{error}</span>
          </div>
        )}

        <button
          type="button"
          onClick={startGoogleAdminLogin}
          className="w-full h-10 border border-[#E2E8F0] hover:bg-[#F8FAFC] text-[#0F1C2E] font-medium rounded text-sm flex items-center justify-center gap-2"
          data-testid="admin-google-login-btn"
        >
          <GoogleIcon className="w-4 h-4" />
          Continuar com Google
        </button>
        <p className="text-[10px] text-[#94A3B8] text-center mt-1.5">
          Apenas e-mails autorizados como admin podem entrar.
        </p>

        <div className="flex items-center gap-2 my-4" aria-hidden="true">
          <div className="flex-1 h-px bg-[#E2E8F0]" />
          <span className="text-[10px] text-[#94A3B8] uppercase tracking-wide">ou</span>
          <div className="flex-1 h-px bg-[#E2E8F0]" />
        </div>

        <form onSubmit={onSubmit} className="space-y-3">
          <label className="block">
            <span className="text-[11px] font-medium text-[#475569] uppercase tracking-wider">E-mail</span>
            <input
              type="email"
              required
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="username"
              className="mt-1 w-full px-3 py-2 border border-[#E2E8F0] rounded text-sm focus:outline-none focus:border-[#1E3A5F]"
              data-testid="admin-login-email"
            />
          </label>
          <label className="block">
            <span className="text-[11px] font-medium text-[#475569] uppercase tracking-wider">Senha</span>
            <div className="relative mt-1">
              <input
                type={showPwd ? 'text' : 'password'}
                required
                value={senha}
                onChange={(e) => setSenha(e.target.value)}
                autoComplete="current-password"
                className="w-full px-3 py-2 pr-10 border border-[#E2E8F0] rounded text-sm focus:outline-none focus:border-[#1E3A5F]"
                data-testid="admin-login-password"
              />
              <button
                type="button"
                onClick={() => setShowPwd((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#475569]"
                aria-label={showPwd ? 'Esconder senha' : 'Mostrar senha'}
              >
                {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </label>
          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-[#1E3A5F] hover:bg-[#16304b] text-white font-bold py-2 rounded text-sm disabled:opacity-60 flex items-center justify-center"
            data-testid="admin-login-submit"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Entrar'}
          </button>
        </form>
        <Link
          to="/admin/forgot-password"
          className="mt-4 block text-center text-xs text-[#1E3A5F] hover:underline"
          data-testid="admin-login-forgot"
        >
          Esqueci minha senha
        </Link>
      </div>
    </div>
  );
};

export default AdminLogin;
